import React, { useEffect } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useInViewAnimation } from '../hooks/useInViewAnimation';
import { automations } from '../data/automations';
import { blueprints } from '../data/blueprints';
import { WorkflowCanvas } from './WorkflowCanvas';

// /automations: the machinery behind the products. Every graph on this page is
// a pipeline that runs on a schedule in this repo or in the jobhunter repo.
// The section that used to sit on the homepage showed live counters and read
// "0 leads this week" on a slow week, which is worse than showing nothing. This
// page shows the shape of the work instead, which is true whatever the week
// looked like.

const Automation = ({ automation, index, onNavigateCaseStudy }) => {
  const { ref, isInView } = useInViewAnimation();

  return (
    <section
      ref={ref}
      className={`py-12 border-t border-[#E0EBF0] ${isInView ? 'animate-fade-in-up' : 'opacity-0'}`}
      style={{ animationDelay: `${0.05 * index}s` }}
      data-testid={`automation-${automation.slug}`}
    >
      <div className="flex flex-col md:flex-row md:items-baseline md:justify-between gap-2 mb-3">
        <h2 className="text-2xl text-[#051A24]">{automation.name}</h2>
        <p className="font-mono text-xs uppercase tracking-[0.15em] text-[#273C46]/70">{automation.schedule}</p>
      </div>

      <p className="text-[#273C46] leading-relaxed max-w-2xl mb-6">{automation.summary}</p>

      <WorkflowCanvas automation={automation} className="mb-6 -mx-6 px-6 md:mx-0 md:px-0" />

      <dl className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-sm">
        <div>
          <dt className="font-mono text-xs uppercase tracking-[0.15em] text-[#273C46]/70 mb-1">Replaces</dt>
          <dd className="text-[#051A24]">{automation.replaces}</dd>
        </div>
        <div>
          <dt className="font-mono text-xs uppercase tracking-[0.15em] text-[#273C46]/70 mb-1">Runs on</dt>
          <dd className="text-[#051A24]">{automation.runsOn.join(', ')}</dd>
        </div>
        <div>
          <dt className="font-mono text-xs uppercase tracking-[0.15em] text-[#273C46]/70 mb-1">Defined in</dt>
          <dd className="text-[#051A24] break-words">{automation.source}</dd>
        </div>
      </dl>

      {automation.caseStudy && onNavigateCaseStudy && (
        <button
          type="button"
          onClick={() => onNavigateCaseStudy(automation.caseStudy)}
          className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-[#051A24] hover:gap-2.5 transition-all min-h-[44px]"
          data-testid={`automation-case-study-${automation.slug}`}
        >
          Read the case study <ArrowRight className="w-4 h-4" />
        </button>
      )}
    </section>
  );
};

// Designs, not deployments. Each one carries the controls that make it
// survivable in a company that has auditors, because the graph is the easy half.
const Blueprint = ({ blueprint, index }) => {
  const { ref, isInView } = useInViewAnimation();

  return (
    <section
      ref={ref}
      className={`py-12 border-t border-[#E0EBF0] ${isInView ? 'animate-fade-in-up' : 'opacity-0'}`}
      style={{ animationDelay: `${0.05 * index}s` }}
      data-testid={`blueprint-${blueprint.slug}`}
    >
      <div className="flex flex-wrap items-center gap-3 mb-2">
        <h3 className="text-2xl text-[#051A24]">{blueprint.name}</h3>
        <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-[#E0EBF0] text-[#273C46]">
          Design, not deployed
        </span>
      </div>
      <p className="font-mono text-xs uppercase tracking-[0.15em] text-[#273C46]/70 mb-4">{blueprint.subtitle}</p>

      <p className="text-[#273C46] leading-relaxed max-w-2xl mb-6">{blueprint.summary}</p>

      <WorkflowCanvas automation={blueprint} className="mb-6 -mx-6 px-6 md:mx-0 md:px-0" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.15em] text-[#273C46]/70 mb-3">Controls</p>
          <ul className="space-y-2 text-sm text-[#051A24]">
            {blueprint.controls.map((control) => (
              <li key={control} className="flex gap-2.5">
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#34D399] flex-shrink-0" />
                <span>{control}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="space-y-6 text-sm">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.15em] text-[#273C46]/70 mb-2">On the bad day</p>
            <p className="text-[#051A24] leading-relaxed">{blueprint.failure}</p>
          </div>
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.15em] text-[#273C46]/70 mb-2">Replaces</p>
            <p className="text-[#051A24]">{blueprint.replaces}</p>
          </div>
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.15em] text-[#273C46]/70 mb-2">Runs on</p>
            <p className="text-[#051A24]">{blueprint.runsOn.join(', ')}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export const AutomationsPage = ({ onNavigateHome, onOpenContact, onNavigateCaseStudy }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
    const previous = document.title;
    document.title = 'Automations — Brandon Crenshaw';
    return () => {
      document.title = previous;
    };
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="automations-page">
      <div className="max-w-[1100px] mx-auto px-6 pt-10">
        <button
          type="button"
          onClick={onNavigateHome}
          className="inline-flex items-center gap-2 text-sm text-[#273C46] hover:text-[#051A24] transition-colors py-2"
        >
          <ArrowLeft className="w-4 h-4" /> Back home
        </button>
      </div>

      <header className="max-w-[1100px] mx-auto px-6 pt-8 pb-8">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#273C46]/70 mb-3">Automations</p>
        <h1 className="text-4xl md:text-5xl text-[#051A24] mb-4">
          Work that{' '}
          <span style={{ fontFamily: "'Playfair Display', serif" }} className="italic">
            runs itself
          </span>
        </h1>
        <p className="text-[#273C46] max-w-2xl text-lg">
          Products are the visible half. These are the pipelines behind them: what triggers, what the model is
          allowed to decide, where a person still signs off. Every graph below is a pipeline that runs on a
          schedule, drawn from the workflow files themselves rather than a screenshot that goes stale. Below
          them are the blueprints: production designs I have not built, labelled as designs.
        </p>
      </header>

      <div className="max-w-[1100px] mx-auto px-6">
        {automations.map((automation, index) => (
          <Automation
            key={automation.slug}
            automation={automation}
            index={index}
            onNavigateCaseStudy={onNavigateCaseStudy}
          />
        ))}
      </div>

      <section className="max-w-[1100px] mx-auto px-6 pt-16" data-testid="blueprints">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#273C46]/70 mb-3">Blueprints</p>
        <h2 className="text-3xl md:text-4xl text-[#051A24] mb-4">
          What I would build{' '}
          <span style={{ fontFamily: "'Playfair Display', serif" }} className="italic">
            inside your company
          </span>
        </h2>
        <p className="text-[#273C46] max-w-2xl text-lg">
          None of these is running today, and the label on each one says so. They are the designs I would ship
          into production: where the model decides, where it is not allowed to, what happens on the day a
          provider is down, and what an auditor can read afterwards. The graph is the easy half. The controls
          under it are the reason one of these survives contact with a real company.
        </p>
      </section>

      <div className="max-w-[1100px] mx-auto px-6">
        {blueprints.map((blueprint, index) => (
          <Blueprint key={blueprint.slug} blueprint={blueprint} index={index} />
        ))}
      </div>

      <section className="max-w-[1100px] mx-auto px-6 py-14 border-t border-[#E0EBF0]">
        <h2 className="text-2xl text-[#051A24] mb-2">Start with the one that is costing you most</h2>
        <p className="text-[#273C46] max-w-2xl mb-8">
          Tell me the process, roughly how much of it runs through a person today, and what happens when it goes
          wrong. I will come back with the graph, the controls, and whether it is worth automating at all.
        </p>
        <button
          type="button"
          onClick={onOpenContact}
          className="inline-flex items-center gap-2 bg-[#051A24] text-[#F6FCFF] px-6 py-3 rounded-full font-medium hover:gap-3 transition-all min-h-[44px]"
          data-testid="automations-contact"
        >
          Tell me what you would automate <ArrowRight className="w-4 h-4" />
        </button>
      </section>

    </div>
  );
};

export default AutomationsPage;
